import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { RichMediaInput, RichMediaState, useRichInput } from '@bonusly/common-action-input';
import { Box } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

interface CollaboratorSelectorProps {
	value: Recipient[];
	onSearch: (query: string) => Promise<Recipient[]>;
	onSelect: (collaborator: Recipient) => void;
	onRemove: (collaborator: Recipient) => void;
	placeholder?: string;
}

export interface CollaboratorSelectorRef {
	getRecipients: () => any[];
	clear: () => void;
}

export const CollaboratorSelector = forwardRef<CollaboratorSelectorRef, CollaboratorSelectorProps>(
	({ value, onSearch, onSelect, onRemove, placeholder = 'Type to add a collaborator...' }, ref) => {
		const previousRecipientsRef = useRef<Recipient[]>([]);

		// Convert current value to content format expected by useRichInput (same as feedback modal)
		const content =
			value.length > 0
				? {
						type: 'doc',
						content: [
							{
								type: 'paragraph',
								content: value
									.map((collaborator) => [
										{
											type: 'recipient',
											attrs: {
												id: collaborator.username, // Use username as ID like feedback modal
												record: collaborator, // Pass full record like feedback modal
											},
										},
										{
											type: 'text',
											text: ' ',
										},
									])
									.flat(),
							},
						],
					}
				: undefined;

		// Adapter function to bridge the different function signatures
		const fetchRecipients = async ({ query }: { query: string }) => {
			const results = await onSearch(query);
			// Filter out already selected collaborators
			return results.filter((result) => !value.find((selected) => selected.id === result.id || selected.username === result.username));
		};

		const { editor, uniqueId } = useRichInput({
			content,
			modules: ['recipient'],
			fetchRecipients,
			placeholder,
			options: {
				recipient: {
					matchWithoutTrigger: true,
				},
			},
		});

		// Monitor editor changes and sync with parent callbacks
		useEffect(() => {
			if (!editor) return;

			const handleUpdate = () => {
				const data = RichMediaState.parse(editor);
				const currentRecipients = data.recipients;
				const previousRecipients = previousRecipientsRef.current;

				// Check for additions
				currentRecipients.forEach((recipient) => {
					if (!previousRecipients.find((prev) => prev.id === recipient.id)) {
						onSelect(recipient);
					}
				});

				// Check for removals
				previousRecipients.forEach((recipient) => {
					if (!currentRecipients.find((curr) => curr.id === recipient.id)) {
						onRemove(recipient);
					}
				});

				// Update the ref for next comparison
				previousRecipientsRef.current = currentRecipients;
			};

			// Listen for editor content changes
			editor.on('update', handleUpdate);
			editor.on('selectionUpdate', handleUpdate);

			// Cleanup
			return () => {
				editor.off('update', handleUpdate);
				editor.off('selectionUpdate', handleUpdate);
			};
		}, [editor, onSelect, onRemove]);

		// Update ref when value prop changes (external updates)
		useEffect(() => {
			previousRecipientsRef.current = value;
		}, [value]);

		// Expose methods to parent via ref
		useImperativeHandle(
			ref,
			() => ({
				getRecipients: () => {
					if (editor) {
						const data = RichMediaState.parse(editor);
						return data.recipients;
					}
					return [];
				},
				clear: () => {
					if (editor) {
						editor.commands.clearContent();
					}
				},
			}),
			[editor]
		);

		return (
			<Box
				border="1px solid"
				borderColor="gray.300"
				borderRadius="md"
				_focusWithin={{
					borderColor: 'blue.500',
					boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
				}}
				minH="40px"
			>
				<RichMediaInput editor={editor} uniqueId={uniqueId} fontSize="md" />
			</Box>
		);
	}
);

CollaboratorSelector.displayName = 'CollaboratorSelector';
